import { partnerComesToYou } from './partnerComesToYou';
import type { PartnerOption } from '../data/getGroupPartners';

/** One partner's computed round-trip transport cost, used to pick a cheaper alternative. */
export type TransportCandidate = {
    /** Whatever `partnerComesToYou` needs to decide, plus the id to report back. */
    partner: Pick<PartnerOption, 'activityId'> & Parameters<typeof partnerComesToYou>[0];
    /** Round-trip transport cost in EUR for this partner to the customer. */
    transportEUR: number;
};

export type CheaperAlternative = {
    activityId: number;
    /** EUR saved versus the currently-selected partner's transport. */
    savings: number;
};

/**
 * Pick the cheapest-transport alternative to the selected partner in custom mode
 * ("Aanbieder komt naar mij").
 *
 * Regression guard: the candidate set MUST be restricted to partners that actually travel to
 * the customer, via `partnerComesToYou` — the flags, not the presence of a tariff. Having no
 * tariff means travelling for free, not staying home.
 *
 * The alternative is only returned when it beats the selected partner's transport
 * cost by more than `threshold` (a fraction, e.g. 0.2 = 20%).
 */
export function selectCheaperAlternative(args: {
    candidates: ReadonlyArray<TransportCandidate>;
    selectedActivityId: number;
    selectedTransportEUR: number;
    threshold: number;
}): CheaperAlternative | null {
    const { candidates, selectedActivityId, selectedTransportEUR, threshold } = args;

    let best: TransportCandidate | null = null;
    for (const candidate of candidates) {
        if (candidate.partner.activityId === selectedActivityId) continue;
        // Never recommend a partner that does not travel to the customer.
        if (!partnerComesToYou(candidate.partner)) continue;
        if (!best || candidate.transportEUR < best.transportEUR) best = candidate;
    }
    if (!best) return null;

    const savings = selectedTransportEUR - best.transportEUR;
    if (savings <= 0) return null;
    // The tolerance absorbs float noise: €0.72 vs €0.90 is exactly 20% but subtracts to a hair more.
    // Costs are not whole cents, so rounding either side would misjudge real savings near 20%.
    if (savings - selectedTransportEUR * threshold <= 1e-9) return null;

    return { activityId: best.partner.activityId, savings };
}
